import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatPrice } from '../../utils/currency';
import Button from '../ui/Button';
import Input from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { Fragrance } from '../fragrance/FragranceCard';

interface RandomPickerFilters {
  house?: string;
  concentration?: string;
  notes_include?: string;
  notes_exclude?: string;
  min_overall_rating?: number;
  max_overall_rating?: number;
  is_favorite?: boolean;
  is_decant?: boolean;
  seasonal_threshold?: number;
  winter_rating?: boolean;
  spring_rating?: boolean;
  summer_rating?: boolean;
  fall_rating?: boolean;
  day_rating?: boolean;
  night_rating?: boolean;
  min_longevity?: number;
  max_longevity?: number;
  min_sillage?: number;
  max_sillage?: number;
}

const RandomScentPicker: React.FC = () => {
  const { user } = useAuth();
  
  // Independent state for random picker - NOT using paginated collection state
  const [fullCollection, setFullCollection] = useState<Fragrance[]>([]);
  const [isLoadingCollection, setIsLoadingCollection] = useState(false);
  const [selectedFragrance, setSelectedFragrance] = useState<Fragrance | null>(null);
  const [isPickingRandom, setIsPickingRandom] = useState(false);
  const [filters, setFilters] = useState<RandomPickerFilters>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filteredPool, setFilteredPool] = useState<Fragrance[]>([]);
  const [rerollCount, setRerollCount] = useState(0);
  const longevityLabels = {
    1: "Very Weak",
    2: "Weak",
    3: "Moderate",
    4: "Long Lasting",
    5: "Eternal",
  };
  
  const sillageLabels = {
    1: "Intimate",
    2: "Moderate",
    3: "Strong",
    4: "Enormous",
  };

  const { token } = useAuth();

  // Fetch entire collection independently
  const fetchFullCollection = async () => {
    if (!token) return;
    
    try {
      setIsLoadingCollection(true);
      
      // Create axios instance for API calls
      const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;
      const response = await axios.get(`${API_BASE_URL}/fragrances/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setFullCollection(response.data.data.fragrances);
      } else {
        console.error('Failed to fetch full collection:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching full collection:', error);
    } finally {
      setIsLoadingCollection(false);
    }
  };

  // Fetch entire collection on mount
  useEffect(() => {
    if (token) {
      fetchFullCollection();
    }
  }, [token]);

  // Update filtered pool when full collection or filters change
  useEffect(() => {
    if (fullCollection.length > 0) {
      const filtered = applyFilters(fullCollection, filters);
      setFilteredPool(filtered);
    }
  }, [fullCollection, filters]);

  const applyFilters = (fragrances: Fragrance[], activeFilters: RandomPickerFilters): Fragrance[] => {
    let filtered = [...fragrances];

    // Apply house filter
    if (activeFilters.house) {
      filtered = filtered.filter(f => 
        f.house.toLowerCase().includes(activeFilters.house!.toLowerCase())
      );
    }

    // Apply concentration filter
    if (activeFilters.concentration) {
      filtered = filtered.filter(f => f.concentration === activeFilters.concentration);
    }

    // Apply notes filters
    if (activeFilters.notes_include) {
      const includeNotes = activeFilters.notes_include.toLowerCase().split(',').map(n => n.trim());
      filtered = filtered.filter(f => {
        const allNotes = [f.notes_top, f.notes_middle, f.notes_base]
          .filter(Boolean)
          .join(', ')
          .toLowerCase();
        return includeNotes.some(note => allNotes.includes(note));
      });
    }

    if (activeFilters.notes_exclude) {
      const excludeNotes = activeFilters.notes_exclude.toLowerCase().split(',').map(n => n.trim());
      filtered = filtered.filter(f => {
        const allNotes = [f.notes_top, f.notes_middle, f.notes_base]
          .filter(Boolean)
          .join(', ')
          .toLowerCase();
        return !excludeNotes.some(note => allNotes.includes(note));
      });
    }

    // Apply overall rating filters
    if (activeFilters.min_overall_rating) {
      filtered = filtered.filter(f => f.overall_rating && f.overall_rating >= activeFilters.min_overall_rating!);
    }

    if (activeFilters.max_overall_rating) {
      filtered = filtered.filter(f => f.overall_rating && f.overall_rating <= activeFilters.max_overall_rating!);
    }

    // Apply favorite filter
    if (activeFilters.is_favorite !== undefined) {
      filtered = filtered.filter(f => Boolean(f.is_favorite) === Boolean(activeFilters.is_favorite));
    }

    // Apply decant filter
    if (activeFilters.is_decant !== undefined) {
      filtered = filtered.filter(f => Boolean(f.is_decant) === Boolean(activeFilters.is_decant));
    }

    // Apply seasonality filters (dynamic threshold)
    const seasonalThreshold = activeFilters.seasonal_threshold || 3;
    
    if (activeFilters.winter_rating) {
      filtered = filtered.filter(f => f.winter_rating && f.winter_rating >= seasonalThreshold);
    }

    if (activeFilters.spring_rating) {
      filtered = filtered.filter(f => f.spring_rating && f.spring_rating >= seasonalThreshold);
    }

    if (activeFilters.summer_rating) {
      filtered = filtered.filter(f => f.summer_rating && f.summer_rating >= seasonalThreshold);
    }

    if (activeFilters.fall_rating) {
      filtered = filtered.filter(f => f.fall_rating && f.fall_rating >= seasonalThreshold);
    }

    // Apply day/night filters (dynamic threshold)
    if (activeFilters.day_rating) {
      filtered = filtered.filter(f => f.day_rating && f.day_rating >= seasonalThreshold);
    }

    if (activeFilters.night_rating) {
      filtered = filtered.filter(f => f.night_rating && f.night_rating >= seasonalThreshold);
    }

    // Apply longevity filters
    if (activeFilters.min_longevity) {
      filtered = filtered.filter(f => f.longevity && f.longevity >= activeFilters.min_longevity!);
    }

    if (activeFilters.max_longevity) {
      filtered = filtered.filter(f => f.longevity && f.longevity <= activeFilters.max_longevity!);
    }

    // Apply sillage filters
    if (activeFilters.min_sillage) {
      filtered = filtered.filter(f => f.sillage && f.sillage >= activeFilters.min_sillage!);
    }

    if (activeFilters.max_sillage) {
      filtered = filtered.filter(f => f.sillage && f.sillage <= activeFilters.max_sillage!);
    }

    return filtered;
  };

  const pickRandomFragrance = () => {
    if (filteredPool.length === 0) return;
  
    setIsPickingRandom(true);
  
    // simulate animation/delay
    setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * filteredPool.length);
      const selected = filteredPool[randomIndex];
      setSelectedFragrance(selected);
  
      // increment reroll count if user is picking again
      setRerollCount(prev => (selectedFragrance ? prev + 1 : prev));
  
      setIsPickingRandom(false);
    }, 500); // adjust delay as needed
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value || undefined };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== undefined && v !== null && v !== '').length;

  const renderStarRating = (rating?: number, maxRating: number = 5) => {
    if (rating == null || rating === 0) return null;
  
    const numericRating = Number(rating);
  
      return (
        <div className="flex items-center space-x-0.5 sm:space-x-1">
         {Array.from({ length: maxRating }, (_, index) => {
          const diff = numericRating - index;
          let fillPercent = 0;
  
          if (diff >= 1) fillPercent = 100;
          else if (diff > 0) {
            // Adjust quarter steps for smoother visual fill
            if (diff <= 0.25) fillPercent = 37;   // barely visible
            else if (diff <= 0.5) fillPercent = 50; // half star
            else if (diff <= 0.75) fillPercent = 60; // almost full
            else fillPercent = 100;
          }
  
           return (
             <div key={index} className="relative w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0">
               {/* Empty gray star (outline) */}
               <svg
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="none"
                 stroke="currentColor"
                 strokeWidth="1.5"
                 className="w-full h-full text-gray-300 dark:text-gray-600"
               >
                 <path
                   strokeLinecap="round"
                   strokeLinejoin="round"
                   d="M12 .587l3.668 7.568L24 9.75l-6 5.847L19.335 24 12 19.897 4.665 24 6 15.597 0 9.75l8.332-1.595L12 .587z"
                 />
               </svg>
   
               {/* Yellow filled portion */}
               {fillPercent > 0 && (
                 <div
                   className="absolute inset-0 overflow-hidden"
                   style={{ clipPath: `inset(0 ${100 - fillPercent}% 0 0)` }}
                 >
                   <svg
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     fill="currentColor"
                     className="w-full h-full text-yellow-400"
                   >
                     <path d="M12 .587l3.668 7.568L24 9.75l-6 5.847L19.335 24 12 19.897 4.665 24 6 15.597 0 9.75l8.332-1.595L12 .587z" />
                   </svg>
                 </div>
               )}
             </div>
           );
        })}
      </div>
    );
  };

  const getSizeDisplay = (fragrance: Fragrance) => {
    const sizes = [];
    if (fragrance.size_oz) sizes.push(`${fragrance.size_oz} oz`);
    if (fragrance.size_ml) sizes.push(`${fragrance.size_ml} ml`);
    return sizes.join(' / ');
  };

  const getLongevityLabel = (rating?: number) => {
    if (!rating) return '';
    return ['', 'Very Weak', 'Weak', 'Moderate', 'Long Lasting', 'Eternal'][rating] || '';
  };

  const getSillageLabel = (rating?: number) => {
    if (!rating) return '';
    return ['', 'Intimate', 'Moderate', 'Strong', 'Enormous'][rating] || '';
  };

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 text-center"
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          🎲 Random Scent Picker
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Let fate choose your next fragrance from your collection
        </p>
      </motion.div>

       {/* Collection Stats */}
       {fullCollection.length > 0 && (
         <motion.div
           initial={{ opacity: 0, y: 20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.1 }}
           className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
         >
           <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
             <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
               {fullCollection.length}
             </div>
             <div className="text-sm text-gray-600 dark:text-gray-400">
               Total Collection
             </div>
           </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {filteredPool.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Available Pool
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
          <motion.div
              key={rerollCount} // triggers animation whenever rerollCount changes
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="text-2xl font-bold text-green-600 dark:text-green-400"
            >
              {rerollCount}
            </motion.div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Re-rolls
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Filter Selection Pool
          </h3>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {filteredPool.length} fragrances available
            </span>
            <Button
              variant="outline"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="relative"
              size="sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
               <div className="pt-6 border-t border-gray-200 dark:border-gray-700 space-y-8 px-3">
                {/* Basic Filters Section */}
                <div className="space-y-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">Basic Filters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* House Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        House
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Chanel, Dior"
                        value={filters.house || ''}
                        onChange={(e) => handleFilterChange('house', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                      />
                    </div>

                    {/* Concentration Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Concentration
                      </label>
                      <select
                        value={filters.concentration || ''}
                        onChange={(e) => handleFilterChange('concentration', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                      >
                        <option value="">All Concentrations</option>
                        <option value="EdC">Eau de Cologne</option>
                        <option value="EdT">Eau de Toilette</option>
                        <option value="EdP">Eau de Parfum</option>
                        <option value="Parfum">Parfum</option>
                        <option value="Elixir">Elixir</option>
                        <option value="Extrait">Extrait de Parfum</option>
                      </select>
                    </div>

                    {/* Overall Rating Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Overall Rating Range
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={filters.min_overall_rating || ''}
                          onChange={(e) => handleFilterChange('min_overall_rating', e.target.value ? parseInt(e.target.value) : undefined)}
                          className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        >
                          <option value="">Min</option>
                          {Array.from({ length: 10 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>{i + 1}</option>
                          ))}
                        </select>
                        <select
                          value={filters.max_overall_rating || ''}
                          onChange={(e) => handleFilterChange('max_overall_rating', e.target.value ? parseInt(e.target.value) : undefined)}
                          className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        >
                          <option value="">Max</option>
                          {Array.from({ length: 10 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>{i + 1}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Filters Section */}
                <div className="space-y-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">Notes Filters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Include Notes (comma-separated)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., vanilla, sandalwood"
                        value={filters.notes_include || ''}
                        onChange={(e) => handleFilterChange('notes_include', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Exclude Notes (comma-separated)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., rose, oud"
                        value={filters.notes_exclude || ''}
                        onChange={(e) => handleFilterChange('notes_exclude', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>

                {/* Collection Type Filters Section */}
                <div className="space-y-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">Collection Type Filters</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={filters.is_favorite || false}
                        onChange={(e) => handleFilterChange('is_favorite', e.target.checked)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">❤️ Favorites</span>
                    </label>
                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={filters.is_decant || false}
                        onChange={(e) => handleFilterChange('is_decant', e.target.checked)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🧪 Decants</span>
                    </label>
                  </div>
                </div>

                {/* Seasonality & Time Filters Section */}
                <div className="space-y-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">Seasonality & Time Preferences</h4>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 space-y-6">
                    {/* Seasonality Threshold */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Minimum Seasonal Rating Threshold
                      </label>
                      <select
                        value={filters.seasonal_threshold || 3}
                        onChange={(e) => handleFilterChange('seasonal_threshold', parseInt(e.target.value))}
                        className="w-full max-w-xs px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                      >
                        <option value={1}>≥1 (Any Rating)</option>
                        <option value={2}>≥2 (Fair or Better)</option>
                        <option value={3}>≥3 (Good or Better)</option>
                        <option value={4}>≥4 (Very Good or Better)</option>
                        <option value={5}>≥5 (Excellent Only)</option>
                      </select>
                    </div>

                    {/* Seasonality Filters */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Seasons
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                          <input
                            type="checkbox"
                            checked={filters.winter_rating || false}
                            onChange={(e) => handleFilterChange('winter_rating', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">❄️ Winter</span>
                        </label>
                        <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                          <input
                            type="checkbox"
                            checked={filters.spring_rating || false}
                            onChange={(e) => handleFilterChange('spring_rating', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🌸 Spring</span>
                        </label>
                        <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                          <input
                            type="checkbox"
                            checked={filters.summer_rating || false}
                            onChange={(e) => handleFilterChange('summer_rating', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">☀️ Summer</span>
                        </label>
                        <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                          <input
                            type="checkbox"
                            checked={filters.fall_rating || false}
                            onChange={(e) => handleFilterChange('fall_rating', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🍂 Fall</span>
                        </label>
                      </div>
                    </div>

                    {/* Day/Night Filters */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Time of Day
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                          <input
                            type="checkbox"
                            checked={filters.day_rating || false}
                            onChange={(e) => handleFilterChange('day_rating', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">☀️ Day</span>
                        </label>
                        <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                          <input
                            type="checkbox"
                            checked={filters.night_rating || false}
                            onChange={(e) => handleFilterChange('night_rating', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🌙 Night</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Filters Section */}
                <div className="space-y-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">Performance Filters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Longevity Range
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          value={filters.min_longevity || ''}
                          onChange={(e) => handleFilterChange('min_longevity', e.target.value ? parseInt(e.target.value) : undefined)}
                          className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        >
                          <option value="">Min</option>
                          {Object.entries(longevityLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <select
                          value={filters.max_longevity || ''}
                          onChange={(e) => handleFilterChange('max_longevity', e.target.value ? parseInt(e.target.value) : undefined)}
                          className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        >
                          <option value="">Max</option>
                          {Object.entries(longevityLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sillage Range
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          value={filters.min_sillage || ''}
                          onChange={(e) => handleFilterChange('min_sillage', e.target.value ? parseInt(e.target.value) : undefined)}
                          className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        >
                          <option value="">Min</option>
                          {Object.entries(sillageLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <select
                          value={filters.max_sillage || ''}
                          onChange={(e) => handleFilterChange('max_sillage', e.target.value ? parseInt(e.target.value) : undefined)}
                          className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                        >
                          <option value="">Max</option>
                          {Object.entries(sillageLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Filter Actions */}
                <div className="flex items-center justify-center space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <Button onClick={clearFilters} variant="outline" className="px-6 py-3">
                    Clear All Filters
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Pick Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-center mb-8"
      >
        <Button
          onClick={pickRandomFragrance}
          disabled={filteredPool.length === 0 || isPickingRandom}
          isLoading={isPickingRandom}
          className="px-8 py-4 text-lg"
          size="lg"
        >
          {isPickingRandom ? (
            <>
              <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Picking...
            </>
          ) : selectedFragrance ? (
            <>
              🎲 Pick Again
            </>
          ) : (
            <>
              🎲 Pick Random Fragrance
            </>
          )}
        </Button>
        
        {activeFilterCount > 0 && fullCollection.length !== 0 && filteredPool.length === 0 && (
          <p className="text-red-600 dark:text-red-400 text-sm mt-2">
            No fragrances match your current filters. Please adjust your criteria.
          </p>
        )}
      </motion.div>

      {/* Selected Fragrance Display */}
      <AnimatePresence mode="wait">
        {selectedFragrance ? (
          <motion.div
            key={selectedFragrance.id}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8"
          >
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">✨</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Your Random Pick
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Selected from {filteredPool.length} possible fragrances
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Basic Info */}
              <div className="space-y-6">
                <div className="text-center lg:text-left">
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {selectedFragrance.name}
                  </h3>
                  <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                    by {selectedFragrance.house}
                  </p>
                  
                  <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-4">
                  {selectedFragrance.concentration && (() => {
                      const concentrationMap: Record<string, string> = {
                        EdC: "Eau de Cologne",
                        EdT: "Eau de Toilette",
                        EdP: "Eau de Parfum",
                        Parfum: "Parfum",
                        Elixir: "Elixir",
                        Extrait: "Extrait de Parfum",
                      };

                      const fullName =
                        concentrationMap[selectedFragrance.concentration] ||
                        selectedFragrance.concentration;

                      return (
                        <span className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                          {fullName}
                        </span>
                      );
                    })()}
                    {Boolean(selectedFragrance.is_favorite) && (
                      <span className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full">
                        ❤️ Favorite
                      </span>
                    )}
                    {Boolean(selectedFragrance.is_decant) && (
                      <span className="px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full">
                        🧪 Decant
                      </span>
                    )}
                    {Boolean(selectedFragrance.is_clone) && (
                      <span className="px-3 py-1 text-sm bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 rounded-full">
                        🔄 Clone
                      </span>
                    )}
                  </div>

                  {getSizeDisplay(selectedFragrance) && (
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Size: {getSizeDisplay(selectedFragrance)}
                    </p>
                  )}
                </div>

                {/* Ratings */}
                {selectedFragrance.overall_rating && (
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Overall Rating
                    </h4>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-center lg:justify-start space-y-2 sm:space-y-0 sm:space-x-2">
                      <div className="flex items-center justify-center lg:justify-start overflow-x-auto">
                        {renderStarRating(selectedFragrance.overall_rating, 10)}
                      </div>
                      <span className="text-lg font-bold text-gray-900 dark:text-white text-center sm:text-left">
                        {selectedFragrance.overall_rating}/10
                      </span>
                    </div>
                  </div>
                )}

                {/* Performance */}
                {(selectedFragrance.longevity || selectedFragrance.sillage) && (
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Performance
                    </h4>
                    <div className="space-y-2">
                      {selectedFragrance.longevity && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Longevity:</span>
                          <div className="flex items-center space-x-2">
                            {renderStarRating(selectedFragrance.longevity, 5)}
                            <span className="text-sm text-gray-900 dark:text-white">
                              {getLongevityLabel(selectedFragrance.longevity)}
                            </span>
                          </div>
                        </div>
                      )}
                      {selectedFragrance.sillage && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Sillage:</span>
                          <div className="flex items-center space-x-2">
                            {renderStarRating(selectedFragrance.sillage, 4)}
                            <span className="text-sm text-gray-900 dark:text-white">
                              {getSillageLabel(selectedFragrance.sillage)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Notes & Details */}
              <div className="space-y-6">
              {/* Notes */}
              {(selectedFragrance.notes_top || selectedFragrance.notes_middle || selectedFragrance.notes_base) && (
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Fragrance Notes
                  </h4>
                  <div className="space-y-3">
                    {selectedFragrance.notes_top && (
                      <div>
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Top Notes
                        </span>
                        <p className="text-gray-900 dark:text-white">
                          {selectedFragrance.notes_top}
                        </p>
                      </div>
                    )}
                    {selectedFragrance.notes_middle && (
                      <div>
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Middle Notes
                        </span>
                        <p className="text-gray-900 dark:text-white">
                          {selectedFragrance.notes_middle}
                        </p>
                      </div>
                    )}
                    {selectedFragrance.notes_base && (
                      <div>
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Base Notes
                        </span>
                        <p className="text-gray-900 dark:text-white">
                          {selectedFragrance.notes_base}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Seasonal Ratings */}
              {(selectedFragrance.winter_rating || selectedFragrance.spring_rating || selectedFragrance.summer_rating || selectedFragrance.fall_rating) && (
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Seasonal Ratings
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedFragrance.winter_rating && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-gray-600 dark:text-gray-400 text-center sm:text-left">❄️ Winter:</span>
                        <div className="flex items-center justify-center sm:justify-end overflow-x-auto">
                          {renderStarRating(selectedFragrance.winter_rating, 5)}
                        </div>
                      </div>
                    )}
                    {selectedFragrance.spring_rating && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-gray-600 dark:text-gray-400 text-center sm:text-left">🌸 Spring:</span>
                        <div className="flex items-center justify-center sm:justify-end overflow-x-auto">
                          {renderStarRating(selectedFragrance.spring_rating, 5)}
                        </div>
                      </div>
                    )}
                    {selectedFragrance.summer_rating && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-gray-600 dark:text-gray-400 text-center sm:text-left">☀️ Summer:</span>
                        <div className="flex items-center justify-center sm:justify-end overflow-x-auto">
                          {renderStarRating(selectedFragrance.summer_rating, 5)}
                        </div>
                      </div>
                    )}
                    {selectedFragrance.fall_rating && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-gray-600 dark:text-gray-400 text-center sm:text-left">🍂 Fall:</span>
                        <div className="flex items-center justify-center sm:justify-end overflow-x-auto">
                          {renderStarRating(selectedFragrance.fall_rating, 5)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Day/Night Ratings */}
              {(selectedFragrance.day_rating || selectedFragrance.night_rating) && (
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Time Preferences
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedFragrance.day_rating && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-gray-600 dark:text-gray-400 text-center sm:text-left">☀️ Day:</span>
                        <div className="flex items-center justify-center sm:justify-end overflow-x-auto">
                          {renderStarRating(selectedFragrance.day_rating, 5)}
                        </div>
                      </div>
                    )}
                    {selectedFragrance.night_rating && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-gray-600 dark:text-gray-400 text-center sm:text-left">🌙 Night:</span>
                        <div className="flex items-center justify-center sm:justify-end overflow-x-auto">
                          {renderStarRating(selectedFragrance.night_rating, 5)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Personal Notes */}
              {selectedFragrance.personal_notes && selectedFragrance.personal_notes.trim() && (
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Personal Notes
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {selectedFragrance.personal_notes}
                  </p>
                </div>
              )}

              {/* Clone Info */}
              {Boolean(selectedFragrance.is_clone) && selectedFragrance.original_fragrance && (
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Original Fragrance
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {selectedFragrance.original_fragrance}
                  </p>
                </div>
              )}
            </div>
            </div>
          </motion.div>
         ) : !isLoadingCollection && fullCollection.length === 0 ? (
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             className="text-center py-16"
           >
             <div className="text-6xl mb-4">🎲</div>
             <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
               No fragrances in your collection
             </h3>
             <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
               Add some fragrances to your collection first, then come back to discover random picks!
             </p>
           </motion.div>
        ) : !isLoadingCollection &&!selectedFragrance && !isPickingRandom ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Ready to discover?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              Click the button above to randomly select a fragrance from your collection. 
              Use filters to narrow down your selection pool.
            </p>
          </motion.div>
        ) : null}
      </AnimatePresence>

       {/* Loading State */}
       {isLoadingCollection && (
         <div className="flex items-center justify-center py-16">
           <div className="text-center">
             <LoadingSpinner size="lg" className="mx-auto mb-4" />
             <p className="text-gray-600 dark:text-gray-400">Loading your collection...</p>
           </div>
         </div>
       )}
    </div>
  );
};

export default RandomScentPicker;
